import { useCallback, useEffect, useRef, useState } from 'react'
import mapboxgl, { Map, MapMouseEvent, Marker } from 'mapbox-gl'
import MapboxGeocoder, { Result } from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import * as turf from '@turf/turf'
import { Position } from 'geojson'
import { useAtomValue } from 'jotai'
import { MapPin, Spline, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { BaseMap } from '@app/components/BaseMap/BaseMap'
import { clientLocationAtom } from '@app/state/clientsAtom'
import {
  BASE_MAP_LIGHT_WITH_HOUSE_NUMBERS,
  NEW_ZEALAND_LAT,
  NEW_ZEALAND_LGT,
  NEW_ZEALAND_ZOOM_LEVEL,
} from '@app/app-constants'
import { DocumentUploadFormData } from '../../../documents-upload.types'
import { useDrawPolygon } from './use-draw-polygon'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
mapboxgl.accessToken = MAPBOX_TOKEN

const POINT_ZOOM_LEVEL = 14

const PREVIEW_SOURCE_ID = 'documentsPolygonPreviewSource'
const PREVIEW_FILL_LAYER_ID = 'documentsPolygonPreviewFillLayer'
const PREVIEW_LINE_LAYER_ID = 'documentsPolygonPreviewLineLayer'

// A plain controlled value/onChange pair rather than a react-hook-form prop — shared cleanly
// between Upload and Edit's differently-shaped forms.
export type GeoreferenceValue = DocumentUploadFormData['georeference']

type GeoreferenceStepProps = {
  value: GeoreferenceValue
  onChange: (value: GeoreferenceValue) => void
}

type ActiveTool = 'pin' | 'draw'

export const GeoreferenceStep = ({ value: georeference, onChange }: GeoreferenceStepProps) => {
  const [map, setMap] = useState<Map | null>(null)
  const markerRef = useRef<Marker | null>(null)
  const geocoderContainerRef = useRef<HTMLDivElement>(null)
  // Same client-configured default view every other map in the app opens on — falls back to
  // the NZ-wide constants only when no client is configured yet.
  const clientLocation = useAtomValue(clientLocationAtom)
  // Which mode the two buttons below are in — separate from georeference.type since a fresh
  // Draw session starts with type still NONE (nothing placed yet). Initialised from whatever
  // the document already has (relevant for Edit); Upload always starts NONE -> 'pin'.
  const [activeTool, setActiveTool] = useState<ActiveTool>(
    georeference.type === 'POLYGON' ? 'draw' : 'pin',
  )
  // Bumped by the Clear button while actively drawing, to wipe an in-progress (not yet closed)
  // polygon — see useDrawPolygon's own comment on why this can't just be an `active` toggle.
  const [drawResetSignal, setDrawResetSignal] = useState(0)

  const setPoint = useCallback(
    (lng: number, lat: number, searchLabel: string | null) => {
      setActiveTool('pin')
      onChange({ type: 'POINT', geometry: { type: 'Point', coordinates: [lng, lat] }, searchLabel })
    },
    [onChange],
  )

  const clearGeoreference = () => {
    onChange({ type: 'NONE', geometry: null, searchLabel: null })
    if (activeTool === 'draw') setDrawResetSignal((n) => n + 1)
  }

  const switchToPin = () => {
    if (georeference.type === 'POLYGON')
      onChange({ type: 'NONE', geometry: null, searchLabel: null })
    setActiveTool('pin')
  }

  const switchToDraw = () => {
    if (georeference.type === 'POINT') onChange({ type: 'NONE', geometry: null, searchLabel: null })
    setActiveTool('draw')
  }

  const handlePolygonComplete = useCallback(
    (ring: Position[]) => {
      onChange({
        type: 'POLYGON',
        geometry: { type: 'Polygon', coordinates: [ring as [number, number][]] },
        searchLabel: null,
      })
    },
    [onChange],
  )

  // Only place points while in Pin mode — otherwise a map click meant for the draw tool would
  // also drop a point marker underneath it.
  useEffect(() => {
    if (!map || activeTool !== 'pin') return
    const handleClick = (e: MapMouseEvent) => setPoint(e.lngLat.lng, e.lngLat.lat, null)
    map.on('click', handleClick)
    return () => {
      map.off('click', handleClick)
    }
  }, [map, activeTool, setPoint])

  // Keep the marker in sync with form state, from either a map click or a geocoder result.
  useEffect(() => {
    if (!map) return
    if (
      activeTool === 'pin' &&
      georeference.type === 'POINT' &&
      georeference.geometry?.type === 'Point'
    ) {
      const [lng, lat] = georeference.geometry.coordinates
      if (!markerRef.current) {
        markerRef.current = new mapboxgl.Marker().setLngLat([lng, lat]).addTo(map)
      } else {
        markerRef.current.setLngLat([lng, lat])
      }
      map.flyTo({ center: [lng, lat], zoom: POINT_ZOOM_LEVEL })
    } else {
      markerRef.current?.remove()
      markerRef.current = null
    }
  }, [map, activeTool, georeference])

  // Actively drawing a new polygon: only when in Draw mode and there's nothing already saved
  // (a saved polygon shows as the static preview below instead — Clear first to redraw).
  const isDrawingNew = activeTool === 'draw' && georeference.type === 'NONE'
  useDrawPolygon(map, isDrawingNew, drawResetSignal, handlePolygonComplete)

  // Static, read-only preview of an already-saved polygon (Edit flow) — Clear + redraw rather
  // than drag-editing existing vertices.
  useEffect(() => {
    if (!map) return
    const showPreview =
      activeTool === 'draw' &&
      georeference.type === 'POLYGON' &&
      georeference.geometry?.type === 'Polygon'

    if (showPreview && georeference.geometry?.type === 'Polygon') {
      const data: GeoJSON.Feature<GeoJSON.Polygon> = {
        type: 'Feature',
        properties: {},
        geometry: georeference.geometry,
      }
      const source = map.getSource(PREVIEW_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
      if (source) {
        source.setData(data)
      } else {
        map.addSource(PREVIEW_SOURCE_ID, { type: 'geojson', data })
        map.addLayer({
          id: PREVIEW_FILL_LAYER_ID,
          type: 'fill',
          source: PREVIEW_SOURCE_ID,
          paint: { 'fill-color': '#0B2948', 'fill-opacity': 0.1 },
        })
        map.addLayer({
          id: PREVIEW_LINE_LAYER_ID,
          type: 'line',
          source: PREVIEW_SOURCE_ID,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#0B2948', 'line-width': 2.5 },
        })
      }
      const [minLng, minLat, maxLng, maxLat] = turf.bbox(data)
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 40, duration: 0 },
      )
    } else {
      if (map.getLayer(PREVIEW_FILL_LAYER_ID)) map.removeLayer(PREVIEW_FILL_LAYER_ID)
      if (map.getLayer(PREVIEW_LINE_LAYER_ID)) map.removeLayer(PREVIEW_LINE_LAYER_ID)
      if (map.getSource(PREVIEW_SOURCE_ID)) map.removeSource(PREVIEW_SOURCE_ID)
    }

    return () => {
      if (map.getLayer(PREVIEW_FILL_LAYER_ID)) map.removeLayer(PREVIEW_FILL_LAYER_ID)
      if (map.getLayer(PREVIEW_LINE_LAYER_ID)) map.removeLayer(PREVIEW_LINE_LAYER_ID)
      if (map.getSource(PREVIEW_SOURCE_ID)) map.removeSource(PREVIEW_SOURCE_ID)
    }
  }, [map, activeTool, georeference])

  // Mapbox Geocoder plugin (same library used elsewhere in REx) with our own 'result' listener
  // — the shared GeocoderButton wrapper doesn't surface results back to React.
  useEffect(() => {
    if (!map || !geocoderContainerRef.current) return

    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken as string,
      // @ts-expect-error - @types/mapbox__mapbox-gl-geocoder's mapboxgl type predates mapbox-gl v3
      mapboxgl,
      placeholder: 'Search an address or suburb',
      countries: 'nz',
    })

    const container = geocoderContainerRef.current
    while (container.firstChild) container.firstChild.remove()
    container.appendChild(geocoder.onAdd(map))

    const handleResult = (e: { result: Result }) => {
      const [lng, lat] = e.result.center
      setPoint(lng, lat, e.result.place_name)
    }
    geocoder.on('result', handleResult)

    return () => {
      geocoder.off('result', handleResult)
      geocoder.onRemove()
    }
  }, [map, setPoint])

  const initialCenter: [number, number] =
    georeference.geometry?.type === 'Point'
      ? georeference.geometry.coordinates
      : clientLocation
        ? [clientLocation.longitude, clientLocation.latitude]
        : [NEW_ZEALAND_LGT, NEW_ZEALAND_LAT]
  const initialZoom =
    georeference.type === 'POINT'
      ? POINT_ZOOM_LEVEL
      : (clientLocation?.zoom ?? NEW_ZEALAND_ZOOM_LEVEL)

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-lg font-semibold">Georeference</h3>
        <p className="text-sm text-muted-foreground">
          Optional — search an address, click the map to drop a pin, or draw an area. Skip if not
          applicable.
        </p>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant={activeTool === 'pin' ? 'default' : 'outline'}
          onClick={switchToPin}
        >
          <MapPin className="h-3.5 w-3.5" />
          Pin
        </Button>
        <Button
          type="button"
          size="sm"
          variant={activeTool === 'draw' ? 'default' : 'outline'}
          onClick={switchToDraw}
        >
          <Spline className="h-3.5 w-3.5" />
          Draw area
        </Button>
      </div>

      <div ref={geocoderContainerRef} className="documents-geocoder" />

      {isDrawingNew && (
        <p className="text-xs text-muted-foreground">
          Click the map to place points. Click the last point again to close the area — click a
          point to remove it.
        </p>
      )}

      <div className="relative h-80 rounded-md overflow-hidden border">
        <BaseMap
          initialView={{ center: initialCenter, zoom: initialZoom }}
          style={BASE_MAP_LIGHT_WITH_HOUSE_NUMBERS}
          onMapReady={setMap}
        />
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {georeference.type === 'NONE'
            ? 'No location set — search, click the map, or draw an area'
            : georeference.type === 'POLYGON'
              ? (georeference.searchLabel ?? 'Area drawn')
              : (georeference.searchLabel ??
                (georeference.geometry?.type === 'Point'
                  ? georeference.geometry.coordinates.map((v) => v.toFixed(5)).join(', ')
                  : ''))}
        </span>
        {(georeference.type !== 'NONE' || activeTool === 'draw') && (
          <Button type="button" variant="ghost" size="sm" onClick={clearGeoreference}>
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>
    </div>
  )
}
